import { Box, Flex, Grid, Heading, Text } from "@chakra-ui/react"
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import type { IconType } from "react-icons"
import {
  FiArchive,
  FiCode,
  FiHardDrive,
  FiImage,
  FiKey,
  FiPlayCircle,
  FiShield,
  FiSlash,
} from "react-icons/fi"

import { IconBox, VisionCard } from "@/components/VisionUI"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-system")({
  component: AdminSystem,
})

type AdminSystemPath =
  | "/admin-database"
  | "/api-explorer"
  | "/api-tokens"
  | "/admin-backup"
  | "/admin-audit"
  | "/admin-branding"
  | "/admin-demo"
  | "/admin-ip-bans"
  | "/licenses"

interface AdminSystemTile {
  titleKey: string
  descKey: string
  defaultTitle: string
  defaultDesc: string
  icon: IconType
  to: AdminSystemPath
}

function AdminSystem() {
  const sc = useSemanticColors()
  const { t } = useTranslation("admin")
  const { pathname } = useLocation()
  const isOverview =
    pathname === "/admin-system" || pathname === "/admin-system/"

  const tiles: AdminSystemTile[] = [
    {
      titleKey: "systemMenu.tiles.database",
      defaultTitle: "Database",
      descKey: "systemMenu.tiles.databaseDesc",
      defaultDesc: "Inspect, query, and clean schema data.",
      icon: FiHardDrive,
      to: "/admin-database",
    },
    {
      titleKey: "systemMenu.tiles.apiExplorer",
      defaultTitle: "API Explorer",
      descKey: "systemMenu.tiles.apiExplorerDesc",
      defaultDesc: "Interactive Swagger-style explorer for the platform API.",
      icon: FiCode,
      to: "/api-explorer",
    },
    {
      titleKey: "systemMenu.tiles.apiTokens",
      defaultTitle: "API Tokens",
      descKey: "systemMenu.tiles.apiTokensDesc",
      defaultDesc: "Issue and revoke programmatic access tokens.",
      icon: FiKey,
      to: "/api-tokens",
    },
    {
      titleKey: "systemMenu.tiles.backup",
      defaultTitle: "Backup and Restore",
      descKey: "systemMenu.tiles.backupDesc",
      defaultDesc: "Snapshot and restore the platform's data.",
      icon: FiArchive,
      to: "/admin-backup",
    },
    {
      titleKey: "systemMenu.tiles.audit",
      defaultTitle: "Audit",
      descKey: "systemMenu.tiles.auditDesc",
      defaultDesc: "Login activity, active sessions, and per-session actions.",
      icon: FiShield,
      to: "/admin-audit",
    },
    {
      titleKey: "cards.branding",
      defaultTitle: "Branding",
      descKey: "cards.brandingDesc",
      defaultDesc: "Upload and manage department logos",
      icon: FiImage,
      to: "/admin-branding",
    },
    {
      titleKey: "cards.demoMode",
      defaultTitle: "Demo Mode",
      descKey: "cards.demoModeDesc",
      defaultDesc:
        "Run this cluster as a public sandbox with periodic data resets.",
      icon: FiPlayCircle,
      to: "/admin-demo",
    },
    {
      titleKey: "systemMenu.tiles.ipBans",
      defaultTitle: "IP Bans and 2SV",
      descKey: "systemMenu.tiles.ipBansDesc",
      defaultDesc: "Failed-login IP banning and two-step verification policy.",
      icon: FiSlash,
      to: "/admin-ip-bans",
    },
    {
      titleKey: "cards.licenses",
      defaultTitle: "Licenses",
      descKey: "cards.licensesDesc",
      defaultDesc:
        "Device-keyed QDatDroid licenses: issue, extend, and revoke.",
      icon: FiKey,
      to: "/licenses",
    },
  ]

  return (
    <>
      {isOverview && (
        <Box>
          <Box mb={8}>
            <Heading size="xl" color={sc.heading} mb={2}>
              {t("systemMenu.title", "System Administration")}
            </Heading>
            <Text color={sc.textMuted}>
              {t(
                "systemMenu.subtitle",
                "Platform-level controls: database, API, backups, audit, branding, demo mode, IP bans, and licenses.",
              )}
            </Text>
          </Box>

          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xl: "repeat(4, 1fr)",
            }}
            gap={4}
          >
            {tiles.map((tile) => (
              <Link
                key={tile.to}
                to={tile.to}
                style={{ textDecoration: "none" }}
              >
                <VisionCard
                  variant="elevated"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "xl",
                  }}
                >
                  <Flex
                    direction="column"
                    align="center"
                    textAlign="center"
                    py={5}
                    px={4}
                  >
                    <IconBox variant="glass" size="lg" mb={3}>
                      <tile.icon fontSize="24px" color={sc.iconColor} />
                    </IconBox>
                    <Heading size="sm" color={sc.heading} mb={1}>
                      {t(tile.titleKey, tile.defaultTitle)}
                    </Heading>
                    <Text color={sc.textMuted} fontSize="xs">
                      {t(tile.descKey, tile.defaultDesc)}
                    </Text>
                  </Flex>
                </VisionCard>
              </Link>
            ))}
          </Grid>
        </Box>
      )}
      <Outlet />
    </>
  )
}
